import { ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
import { Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { Stack, StackProps, styled } from '@mui/material';

import { ReactComponent as AcadlyLogoIcon } from '../../assets/acadly-logo-square.svg';
import ClassHeader from '../../class/ClassHeader';
import { MAIN_CONTENT_BODY_ID } from '../../constants';
import CourseAnalyticsHeader from '../../courses/CourseAnalytics/CourseAnalyticsHeader';
import CourseFooter from '../../courses/CourseFooter';
import CourseHeader from '../../courses/CourseHeader';
import { selectAccessibilityPrefs } from '../../db/app/selectors';
import { AcadlyPage } from '../../db/shared/types';
import DiscussionWordCloudHeader from '../../discussion/DiscussionWordCloud/DiscussionWordCloudHeader';
import HomeHeader from '../../home/HomeHeader';
import { getAcadlyRouteByURL } from '../../pages/helpers';
import routes from '../../pages/routes';
import { useAppSelector } from '../../store/hooks';
import { MAIN_CONTENT_WIDTH } from '../../styles/theme';
import { forMobile } from '../../utils/media-queries';
import PromotionalTip from '../PromotionalTip';
import { LayoutContext } from './Context';

const Root = styled('main')(() => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
}));

const Header = styled('div')(({ theme }) => ({
  flexShrink: 0,
  zIndex: theme.zIndex.pageHeader,
}));

const Body = styled((props: StackProps) => <Stack {...props} />)(() => ({
  flex: 1,
  overflow: 'auto',
}));

const BodyContent = styled('div')(() => ({
  flex: 1,
  width: '100%',
  maxWidth: MAIN_CONTENT_WIDTH,
  margin: 'auto',
}));

const StyledPromotionalTip = styled(PromotionalTip)(({ theme }) => ({
  margin: theme.spacing(4, 0, 0),
  [forMobile(theme)]: {
    margin: theme.spacing(4),
    width: `calc(100% - ${theme.spacing(4 * 2)})`,
  },
}));

const AcadlyLogo = styled(AcadlyLogoIcon)(({ theme }) => ({
  position: 'fixed',
  left: theme.spacing(5),
  bottom: theme.spacing(5),
  width: 24,
  height: 24,
  zIndex: -1,
}));

const Footer = styled('div')(() => ({
  flexShrink: 0,
}));

/**
 * Promotional Tip Paths are used to render promotional tips on every page
 * Commented out paths indicates that those paths render Promotional tips inside the page
 */
const promotionalTipPaths = [
  routes.home.path,
  routes.refer.path,
  routes.archivedCourses.path,
  routes.settings.path,
  // routes.courseTimeline.path,
  routes.courseInfo.path,
  routes.courseSyllabus.path,
  routes.courseAnalyticsStudents.path,
  routes.courseAnalyticsAverages.path,
  routes.courseAnalyticsStudent.path,
  // routes.assignment.path,
  routes.assignmentAnalytics.path,
  // routes.newAnnouncement.path,
  // routes.announcement.path,
  routes.cls.path,
  routes.classActivities.path,
  routes.classAgenda.path,
  routes.classAttendance.path,
  routes.classAnalytics.path,
  // routes.quiz.path,
  routes.quizAnalytics.path,
  // routes.poll.path,
  routes.pollAnalytics.path,
  // routes.discussion.path,
  routes.discussionWordCloud.path,
  // routes.resource.path,
  routes.resourceAnalytics.path,
  routes.newQuery.path,
  // routes.query.path,
] as const;

interface Props {
  className?: string;
  children?: ReactNode;
}

const MainContent = ({ className, children }: Props) => {
  const { turnOff } = useAppSelector((state) => selectAccessibilityPrefs(state));
  const isScreenReaderOn = !turnOff;

  const { selectedTabIds } = useContext(LayoutContext);

  const tabPanelRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const previousRouteRef = useRef<ReturnType<typeof getAcadlyRouteByURL>>();

  const focusTabPanel = useCallback(
    async (pages: AcadlyPage[], previousPage: AcadlyPage, currentPage: AcadlyPage) => {
      // e.g. user was on one of the course page tabs
      if (pages.includes(previousPage)) {
        // e.g. user is on one of the course page tabs
        if (pages.includes(currentPage)) {
          await new Promise((resolve) => setTimeout(resolve, 0));
          tabPanelRef.current?.focus();
        }
      }
    },
    []
  );

  useEffect(() => {
    if (!isScreenReaderOn) return;

    const currentRoute = getAcadlyRouteByURL(location.pathname);
    if (!currentRoute.name) return;

    if (previousRouteRef.current?.name) {
      const courseRoutes = [AcadlyPage.COURSE_TIMELINE, AcadlyPage.COURSE_INFO, AcadlyPage.COURSE_SYLLABUS];
      focusTabPanel(courseRoutes, previousRouteRef.current.name, currentRoute.name);

      const classRoutes = [AcadlyPage.CLASS_ACTIVITIES, AcadlyPage.CLASS_AGENDA, AcadlyPage.CLASS_ATTENDANCE];
      focusTabPanel(classRoutes, previousRouteRef.current.name, currentRoute.name);
    }

    previousRouteRef.current = currentRoute;
  }, [location, focusTabPanel, isScreenReaderOn]);

  return (
    <Root className={className}>
      <Header>
        <Routes>
          <Route path={routes.home.relativePath} element={<HomeHeader />} />
          <Route path={routes.university.relativePath} element={<Outlet />}>
            <Route path={routes.course.relativePath} element={<Outlet />}>
              <Route path={routes.courseTimeline.relativePath} element={<Outlet />}>
                <Route index element={<CourseHeader />} />
                <Route path={routes.cls.relativePath} element={<Outlet />}>
                  <Route path={routes.classActivities.relativePath} element={<Outlet />}>
                    <Route index element={<ClassHeader />} />
                    <Route path={routes.discussions.relativePath} element={<Outlet />}>
                      <Route path={routes.discussion.relativePath} element={<Outlet />}>
                        <Route
                          path={routes.discussionWordCloud.relativePath}
                          element={<DiscussionWordCloudHeader />}
                        />
                      </Route>
                    </Route>
                  </Route>
                  <Route path={routes.classAgenda.relativePath} element={<ClassHeader />} />
                  <Route path={routes.classAttendance.relativePath} element={<ClassHeader />} />
                </Route>
              </Route>
              <Route path={routes.courseInfo.relativePath} element={<CourseHeader />} />
              <Route path={routes.courseSyllabus.relativePath} element={<CourseHeader />} />
              <Route path={routes.courseAnalytics.relativePath} element={<Outlet />}>
                <Route
                  path={routes.courseAnalyticsStudents.relativePath}
                  element={<CourseAnalyticsHeader />}
                />
                <Route
                  path={routes.courseAnalyticsAverages.relativePath}
                  element={<CourseAnalyticsHeader />}
                />
              </Route>
            </Route>
          </Route>
        </Routes>
      </Header>
      <Body id={MAIN_CONTENT_BODY_ID}>
        <BodyContent
          ref={tabPanelRef}
          id={selectedTabIds?.panelId}
          role={selectedTabIds ? 'tabpanel' : 'none'}
          aria-labelledby={selectedTabIds?.tabId}
          tabIndex={isScreenReaderOn ? 0 : undefined}
        >
          <Routes>
            {promotionalTipPaths.map((path) => (
              <Route key={path} path={path} element={<StyledPromotionalTip />} />
            ))}
          </Routes>
          {children}
        </BodyContent>
        <AcadlyLogo aria-hidden />
      </Body>
      <Footer>
        <Routes>
          <Route path={routes.university.relativePath} element={<Outlet />}>
            <Route path={`${routes.course.relativePath}/*`} element={<CourseFooter />} />
          </Route>
        </Routes>
      </Footer>
    </Root>
  );
};

export default MainContent;
